/**
 * User-facing settings: persisted in Dexie (`settings` table, one row with
 * `id = 'user'`) and surfaced as a reactive `$state` module for Svelte
 * components. Written by the `/settings` route; read by every live-analysis
 * consumer (Record, TestVideo, LiveAnalysisLoop).
 *
 * Defaults here must stay in sync with the v3 schema migration in `db.ts`.
 */

import { db } from '../db.js';

export type DetectionFps = 7 | 15 | 30;
export type CalibrationIntervalS = 2 | 4 | 8;
export type Handedness = 'right' | 'left';

export interface OverlayFlags {
  readonly stickman: boolean;
  readonly miniCourt: boolean;
  readonly ball: boolean;
  readonly courtPolygon: boolean;
  readonly ballTrail: boolean;
}

export interface UserSettings {
  /** Identifier. Only one row is ever stored ("user"). */
  readonly id: 'user';
  // --- Contact detection ---
  readonly contactThresholdSpeed: number; // 0.4 .. 2.0
  readonly contactKSigma: number; // 1.0 .. 3.0
  readonly contactRefractoryMs: number; // 300 .. 1000
  // --- Overlays ---
  readonly overlays: OverlayFlags;
  // --- Performance ---
  readonly detectionFps: DetectionFps;
  readonly recalibrateEverySec: CalibrationIntervalS;
  // --- Player ---
  readonly handedness: Handedness;
  // --- Scene change ---
  readonly sceneChangeThreshold: number; // 10 .. 80
  readonly pauseOnCut: boolean;
}

export const DEFAULT_SETTINGS: UserSettings = {
  id: 'user',
  contactThresholdSpeed: 0.8,
  contactKSigma: 2.0,
  contactRefractoryMs: 600,
  overlays: {
    stickman: true,
    miniCourt: true,
    ball: true,
    courtPolygon: true,
    ballTrail: true,
  },
  detectionFps: 15,
  recalibrateEverySec: 4,
  handedness: 'right',
  sceneChangeThreshold: 30,
  pauseOnCut: true,
};

// -----------------------------------------------------------------------------
// Reactive store — Svelte 5 runes. Imported as `settings` by every consumer.
// We mutate fields via `updateSettings` so the store stays observable.
// -----------------------------------------------------------------------------

let _state = $state<UserSettings>({ ...DEFAULT_SETTINGS });
let loaded = false;

/**
 * Svelte-friendly getter that participates in the reactive graph. Usage:
 *
 *   const s = getSettings();
 *   $effect(() => { console.log(s.detectionFps); });
 */
export function getSettings(): UserSettings {
  return _state;
}

/** Non-reactive snapshot — safe inside non-reactive code paths (workers, tests). */
export function snapshotSettings(): UserSettings {
  return { ..._state, overlays: { ..._state.overlays } };
}

/** Patch any subset of fields; persists to Dexie asynchronously. */
export async function updateSettings(patch: Partial<UserSettings>): Promise<void> {
  // Svelte 5 `$state` replacements on nested objects require new references.
  const next: UserSettings = {
    ..._state,
    ...patch,
    id: 'user',
    overlays: { ..._state.overlays, ...(patch.overlays ?? {}) },
  };
  _state = next;
  await persist(next);
}

/**
 * Load the persisted row from Dexie once per session. Safe to call from
 * component `onMount` — subsequent calls no-op.
 */
export async function initSettings(): Promise<void> {
  if (loaded) return;
  loaded = true;
  try {
    const row = await db.settings.get('user');
    if (row) {
      _state = mergeWithDefaults(row);
    } else {
      await persist(DEFAULT_SETTINGS);
    }
  } catch (err) {
    // Do not surface: settings MUST never break the capture loop. Defaults apply.
    console.warn('[settings] load failed, using defaults', err);
  }
}

async function persist(s: UserSettings): Promise<void> {
  try {
    // `SettingsRow` in db.ts carries an open index signature so the schema
    // can accept forward-compatible fields without a migration. Our concrete
    // `UserSettings` interface is closed, so TypeScript rejects the direct
    // assignment. The cast is safe because every UserSettings field is a
    // valid `unknown` under the row's index signature.
    await db.settings.put(s as unknown as import('../db').SettingsRow);
  } catch (err) {
    console.warn('[settings] persist failed', err);
  }
}

/** Defensive merge in case an older row lacks new fields. */
export function mergeWithDefaults(row: Partial<UserSettings>): UserSettings {
  return {
    ...DEFAULT_SETTINGS,
    ...row,
    id: 'user',
    overlays: { ...DEFAULT_SETTINGS.overlays, ...(row.overlays ?? {}) },
  };
}

// -----------------------------------------------------------------------------
// Test helpers — not exported from the public barrel; imported directly by
// unit tests that need to reset reactive state between cases.
// -----------------------------------------------------------------------------

export function __resetForTests(): void {
  _state = { ...DEFAULT_SETTINGS, overlays: { ...DEFAULT_SETTINGS.overlays } };
  loaded = false;
}
